// ✅ src/screens/PublicProfileScreen.js (FULL)

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { auth, db } from "../lib/firebase";

/* ---------------- helpers ---------------- */
function safeStr(v) {
  return String(v ?? "").trim();
}
function msFromTs(ts) {
  try {
    if (!ts) return null;
    if (typeof ts === "number") return ts;
    if (ts?.toMillis) return ts.toMillis();
    if (ts?.toDate) return ts.toDate().getTime();
    return null;
  } catch {
    return null;
  }
}
function calcAgeFromBirthDate(birthDateTs) {
  const ms = msFromTs(birthDateTs);
  if (!ms) return null;
  const dob = new Date(ms);
  const now = new Date();
  let age = now.getFullYear() - dob.getFullYear();
  const m = now.getMonth() - dob.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < dob.getDate())) age -= 1;
  return age;
}
function initials(name) {
  const t = safeStr(name);
  if (!t) return "?";
  const parts = t.split(" ").filter(Boolean);
  const a = parts[0]?.[0] ?? "?";
  const b = parts[1]?.[0] ?? "";
  return (a + b).toUpperCase();
}

/* ---------------- main ---------------- */

export default function PublicProfileScreen() {
  const nav = useNavigate();
  const { uid } = useParams();

  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [photoIndex, setPhotoIndex] = useState(0);

  const myUid = useMemo(() => safeStr(auth.currentUser?.uid), []);

  useEffect(() => {
    let alive = true;

    async function load() {
      if (!uid) return;

      try {
        const snap = await getDoc(doc(db, "datingProfiles", uid));
        if (!alive) return;

        if (!snap.exists()) {
          setProfile(null);
          setLoading(false);
          return;
        }

        const data = snap.data();
        const computedAge = calcAgeFromBirthDate(data.birthDate);
        const age = Number.isFinite(Number(data.age))
          ? Number(data.age)
          : computedAge;

        setProfile({
          uid,
          ...data,
          age,
          photos: Array.isArray(data.photos) ? data.photos : [],
        });

        setLoading(false);
      } catch (err) {
        console.error("Profile load error:", err);
        setLoading(false);
      }
    }

    load();
    return () => {
      alive = false;
    };
  }, [uid]);

  if (loading) {
    return <div style={S.center}>Loading profile...</div>;
  }

  if (!profile) {
    return <div style={S.center}>Profile not found.</div>;
  }

  const photos = profile.photos.length
    ? profile.photos
    : profile.avatar
    ? [profile.avatar]
    : [];

  const currentPhoto = photos[photoIndex];

  return (
    <div style={S.wrapper}>
      {/* Header */}
      <div style={S.header}>
        <button onClick={() => nav(-1)} style={S.backBtn}>←</button>
        <div style={S.headerTitle}>
          {profile.displayName}
          {profile.verified && <span style={S.verified}> ✅</span>}
        </div>
      </div>

      {/* Photo Gallery */}
      <div style={S.photoContainer}>
        {currentPhoto ? (
          <img src={currentPhoto} alt="" style={S.photo} />
        ) : (
          <div style={S.photoFallback}>
            {initials(profile.displayName)}
          </div>
        )}

        {photos.length > 1 && (
          <div style={S.photoControls}>
            <button
              onClick={() =>
                setPhotoIndex((p) => (p === 0 ? photos.length - 1 : p - 1))
              }
            >
              ◀
            </button>
            <button
              onClick={() =>
                setPhotoIndex((p) =>
                  p === photos.length - 1 ? 0 : p + 1
                )
              }
            >
              ▶
            </button>
          </div>
        )}
      </div>

      {/* Basic Info */}
      <div style={S.section}>
        <h2>
          {profile.displayName}
          {profile.age ? ` · ${profile.age}` : ""}
        </h2>

        <p>📍 {profile.city || "Unknown city"}</p>
        <p>🎯 Looking for: {profile.lookingFor || "—"}</p>
        <p>❤️ Goal: {profile.relationshipGoal || "—"}</p>
      </div>

      {/* Bio */}
      {profile.bio && (
        <div style={S.section}>
          <h3>About</h3>
          <p>{profile.bio}</p>
        </div>
      )}

      {/* Interests */}
      {profile.interests?.length > 0 && (
        <div style={S.section}>
          <h3>Interests</h3>
          <div style={S.tags}>
            {profile.interests.map((tag, i) => (
              <span key={i} style={S.tag}>{tag}</span>
            ))}
          </div>
        </div>
      )}

      {/* Lifestyle */}
      <div style={S.section}>
        <h3>Details</h3>
        <p>Height: {profile.height || "—"} cm</p>
        <p>Education: {profile.education || "—"}</p>
        <p>Job: {profile.job || "—"}</p>
        <p>Smoking: {profile.smoking || "—"}</p>
        <p>Drinking: {profile.drinking || "—"}</p>
        <p>Religion: {profile.religion || "—"}</p>
        <p>
          Languages:{" "}
          {profile.languages?.length
            ? profile.languages.join(", ")
            : "—"}
        </p>
      </div>

      {/* Actions */}
      {myUid !== profile.uid && (
        <div style={S.actionRow}>
          <button style={S.primaryBtn} onClick={() => nav(-1)}>
            ❤️ Like
          </button>
        </div>
      )}
    </div>
  );
}

/* ---------------- styles ---------------- */

const S = {
  wrapper: {
    maxWidth: 640,
    margin: "0 auto",
    padding: 16,
    display: "flex",
    flexDirection: "column",
    gap: 18,
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: 10,
  },
  backBtn: {
    fontSize: 20,
    cursor: "pointer",
  },
  headerTitle: {
    fontWeight: 900,
    fontSize: 18,
  },
  verified: {
    color: "green",
  },
  photoContainer: {
    position: "relative",
  },
  photo: {
    width: "100%",
    height: 480,
    objectFit: "cover",
    borderRadius: 20,
  },
  photoFallback: {
    height: 480,
    display: "grid",
    placeItems: "center",
    background: "#111",
    color: "#fff",
    fontSize: 40,
    borderRadius: 20,
  },
  photoControls: {
    position: "absolute",
    bottom: 10,
    right: 10,
    display: "flex",
    gap: 6,
  },
  section: {
    background: "rgba(255,255,255,0.7)",
    padding: 16,
    borderRadius: 16,
  },
  tags: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
  },
  tag: {
    background: "#eee",
    padding: "6px 10px",
    borderRadius: 20,
    fontSize: 12,
  },
  actionRow: {
    display: "flex",
    justifyContent: "center",
  },
  primaryBtn: {
    padding: "12px 24px",
    borderRadius: 999,
    background: "linear-gradient(135deg,#ec4899,#6366f1)",
    color: "#fff",
    border: "none",
    fontWeight: 900,
    cursor: "pointer",
  },
  center: {
    padding: 40,
    textAlign: "center",
    fontWeight: 900,
  },
};
